import { apiClient } from './api';
import type {
  TenantFamilyAthleteCreatePayload,
  TenantFamilyAthleteSummary,
  TenantFamilyBookingRequestPayload,
  TenantFamilyHouseholdResponse,
  TenantFamilyScheduleResponse,
  TenantFamilyNotePayload,
  TenantFamilyNoteTimeline,
  TenantFamilyBookableSlotsResponse,
  TenantBookingHold,
  TenantStripeCheckoutSessionRequest,
  TenantStripeCheckoutSessionResponse,
} from '../types';

export async function getTenantFamilyBookableSlots(
  athleteId: number,
  days: number = 14
): Promise<TenantFamilyBookableSlotsResponse> {
  const response = await apiClient.get<TenantFamilyBookableSlotsResponse>(
    '/api/v1/web/tenant-family/bookable-slots',
    { params: { athleteId, days } }
  );
  return response.data;
}

export async function createTenantFamilyBookingHold(payload: {
  athleteId: number;
  offeringId: number;
  startsAt: string;
  clientMutationId: string;
}): Promise<TenantBookingHold> {
  const response = await apiClient.post<TenantBookingHold>('/api/v1/web/tenant-family/booking-holds', payload);
  return response.data;
}

export async function confirmTenantFamilyOffPlatformBooking(holdId: number): Promise<TenantBookingHold> {
  const response = await apiClient.post<TenantBookingHold>(
    `/api/v1/web/tenant-family/booking-holds/${holdId}/confirm-off-platform`
  );
  return response.data;
}

export async function createTenantFamilyBookingCheckout(
  holdId: number,
  payload: TenantStripeCheckoutSessionRequest
): Promise<TenantStripeCheckoutSessionResponse> {
  const response = await apiClient.post<TenantStripeCheckoutSessionResponse>(
    `/api/v1/web/tenant-family/booking-holds/${holdId}/checkout`,
    payload
  );
  return response.data;
}

export async function getTenantFamilyHousehold(): Promise<TenantFamilyHouseholdResponse> {
  const response = await apiClient.get<TenantFamilyHouseholdResponse>('/api/v1/web/tenant-family/household');
  return response.data;
}

export async function createTenantFamilyAthlete(payload: TenantFamilyAthleteCreatePayload): Promise<TenantFamilyAthleteSummary> {
  const response = await apiClient.post<TenantFamilyAthleteSummary>('/api/v1/web/tenant-family/athletes', payload);
  return response.data;
}

export async function getTenantFamilySchedule(): Promise<TenantFamilyScheduleResponse> {
  const response = await apiClient.get<TenantFamilyScheduleResponse>('/api/v1/web/tenant-family/schedule');
  return response.data;
}

export async function requestTenantFamilyBooking(
  occurrenceId: number,
  payload: TenantFamilyBookingRequestPayload
): Promise<TenantFamilyScheduleResponse> {
  const response = await apiClient.post<TenantFamilyScheduleResponse>(
    `/api/v1/web/tenant-family/schedule/occurrences/${occurrenceId}/bookings`,
    payload
  );
  return response.data;
}

export async function getTenantFamilyNotes(athleteId: number): Promise<TenantFamilyNoteTimeline> {
  const response = await apiClient.get<TenantFamilyNoteTimeline>(
    `/api/v1/web/tenant-family/athletes/${athleteId}/notes`
  );
  return response.data;
}

export async function createTenantFamilyNote(
  athleteId: number,
  payload: TenantFamilyNotePayload
): Promise<TenantFamilyNoteTimeline> {
  const response = await apiClient.post<TenantFamilyNoteTimeline>(
    `/api/v1/web/tenant-family/athletes/${athleteId}/notes`, payload
  );
  return response.data;
}

export async function updateTenantFamilyNote(
  athleteId: number,
  noteId: number,
  payload: TenantFamilyNotePayload
): Promise<TenantFamilyNoteTimeline> {
  const response = await apiClient.patch<TenantFamilyNoteTimeline>(
    `/api/v1/web/tenant-family/athletes/${athleteId}/notes/${noteId}`, payload
  );
  return response.data;
}

export async function deleteTenantFamilyNote(
  athleteId: number,
  noteId: number,
  version: number
): Promise<TenantFamilyNoteTimeline> {
  const response = await apiClient.delete<TenantFamilyNoteTimeline>(
    `/api/v1/web/tenant-family/athletes/${athleteId}/notes/${noteId}`,
    { params: { version } }
  );
  return response.data;
}

export async function skipTenantFamilyIntake(athleteId: number): Promise<TenantFamilyNoteTimeline> {
  const response = await apiClient.post<TenantFamilyNoteTimeline>(
    `/api/v1/web/tenant-family/athletes/${athleteId}/intake/skip`
  );
  return response.data;
}
